import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Swiper } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import { fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, scan, switchMap } from 'rxjs/operators';
import { HTTPSearchResultTour } from 'src/app/models';
import { AnalyticService } from 'src/app/services/analytic.service';
import { ApiService } from 'src/app/services/api.service';
import { pluralize } from '../../pipes/pluralize/pluralize';
import { TemplateReplacePipe } from '../../pipes/trmplate-replace/template-replace.pipe';
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from '../../../config/hotel-tours-list-page.config';
import { TourRecommendationsComponent } from '../tour-recommendations/tour-recommendations.component';

@Component({
  selector: 'app-tour-image',
  templateUrl: './tour-image.component.mobile.html',
  styleUrls: ['./tour-image.component.mobile.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [
    CommonModule,
    TemplateReplacePipe,
    TourRecommendationsComponent,
  ],
  standalone: true,
  schemas: [NO_ERRORS_SCHEMA],
})
export class TourImageMobileComponent implements AfterViewInit, OnDestroy {
  @ViewChild('swiperContainer', { static: false }) swiperContainer?: ElementRef<HTMLElement>;
  @ViewChild('swiperGallery', { static: false }) swiperGallery?: ElementRef<HTMLElement>;
  @ViewChild('swiperBullets', { static: false }) swiperBullets?: ElementRef<HTMLElement>;
  @Input() tour!: HTTPSearchResultTour;
  isShowDiscount = true;
  isLoading = true;

  imageCollection: string[] = [];
  isPhotoSliderAvailable = true;
  isPhotosLoaded = false;
  isPhotoLoading = false;
  isShowBackground = true;

  currentPhotoIndex = 1;

  swipperConfig: SwiperOptions = {
    observer: false,
    direction: 'horizontal',
    spaceBetween: 0,
    slidesPerView: 1,
    centeredSlides: true,
    pagination: false,
    navigation: false,
  };

  swiperSub?: Subscription;
  isSwiperObservableInit = false;
  open$!: Observable<void>;

  private readonly open = new Subject<void>();
  private pendingSlideDirection: 'next' | 'prev' | null = null;
  private swiperInstance: Swiper | null = null;
  private shouldOpenFromSecondSlide = false;

  messages: any = {};

  constructor(
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
    private apiService: ApiService,
    private cdRef: ChangeDetectorRef,
    private render: Renderer2,
    private analyticService: AnalyticService,
  ) {
    this.isShowDiscount = appConfig.isShowDiscount;
    this.isPhotoSliderAvailable = appConfig.isPhotoSliderAvailable;
    this.messages = Object.assign(
        {},
        appConfig.translates.tourImage,
        {
          oncePlural: appConfig.translates.plurals.oncePlural
        }
    );
    this.open$ = this.open.asObservable();
  }

  tourPurchasedMapping(salesCount: number) {
    return {
      salesCountVal: salesCount,
      salesCountText: this.salesCount,
    };
  }

  get salesCount() {
    return pluralize(this.tour.salesCount, this.messages.oncePlural);
  }

  public ngAfterViewInit(): void {
    if (this.appConfig.isPhotoSliderAvailable && this.swiperContainer) {
      this.subscribeToTouchMove();
    }

    this.renderSwiperBullets();
  }

  public swiperInit(): void {
    this.isShowBackground = false;
    setTimeout(() => {
      if (this.swiperContainer) {
        this.render.removeStyle(this.swiperContainer.nativeElement, 'backgroundImage');
      }
    }, 500);

    if (this.swiperSub) {
      this.swiperSub.unsubscribe();
    }

    if (this.shouldOpenFromSecondSlide) {
      this.currentPhotoIndex = 2;
      this.setActiveBullet(this.currentPhotoIndex);
      this.open.next();
      this.shouldOpenFromSecondSlide = false;
    }

    if (this.pendingSlideDirection === 'next') {
      this.pendingSlideDirection = null;
      this.showNextPhoto();
    } else if (this.pendingSlideDirection === 'prev') {
      this.pendingSlideDirection = null;
      this.showPrevPhoto();
    }

    this.analyticService.trackEvent('hottable.swipe-image');
  }

  public onPrevPhoto(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.prevPhoto();
  }

  public onNextPhoto(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.nextPhoto();
  }

  public get isShowPrevButton(): boolean {
    return this.currentPhotoIndex > 1;
  }

  public get mainPhotoSize() {
    return 'md';
  }

  public get mainPhotoSrc() {
    if (this.tour.photo) {
      return `${this.appConfig.photoBasePath}/${this.tour.photo}`;
    }

    return this.appConfig.noPhotoLink;
  }

  public slideNextTransitionStart(): void {
    if (this.currentPhotoIndex === 1) {
      this.open.next();
    }

    this.currentPhotoIndex++;
    this.nextPhotoBulletAnimation();
  }

  public slidePrevTransitionStart(): void {
    this.currentPhotoIndex--;
    this.prevPhotoBulletAnimation();
  }

  private showNextPhoto(): void {
    this.swiperInstance?.slideNext();
  }

  private showPrevPhoto(): void {
    this.swiperInstance?.slidePrev();
  }

  private loadPhoto(direction: 'next' | 'prev'): void {
    if (!this.isPhotosLoaded && !this.isPhotoLoading) {
      this.isPhotoLoading = true;
      this.cdRef.detectChanges();

      this.apiService.loadHotelPhoto(this.tour.hotelId).subscribe((photos: string[]) => {
        this.imageCollection = photos;
        this.isPhotosLoaded = true;
        this.isPhotoLoading = false;

        const canOpenFromSecondSlide = direction === 'next'
          && this.imageCollection.length > 1
          && this.isSamePhoto(this.imageCollection[0], this.mainPhotoSrc);

        if (canOpenFromSecondSlide) {
          this.shouldOpenFromSecondSlide = true;
          this.pendingSlideDirection = null;
        } else {
          this.pendingSlideDirection = direction;
        }

        this.cdRef.detectChanges();

        setTimeout(() => {
          this.initSwiper();
        }, 0);
      });
    }
  }

  private prevPhoto(): void {
    if (this.isPhotosLoaded) {
      this.showPrevPhoto();
    } else {
      this.loadPhoto('prev');
    }
  }

  private nextPhoto(): void {
    if (this.isPhotosLoaded) {
      this.showNextPhoto();
    } else {
      this.loadPhoto('next');
    }
  }

  private subscribeToTouchMove(): void {
    if (!this.swiperContainer) {
      return;
    }

    const toleranceXY = 35;

    const swipe$ = fromEvent(this.swiperContainer.nativeElement, 'touchstart').pipe(
        filter(() => !this.isPhotosLoaded),
        switchMap((startEvent: any) =>
            fromEvent(this.swiperContainer.nativeElement, 'touchmove').pipe(
                map((event: any) => {
                  return { pageX: event.touches[0].pageX, pageY: event.touches[0].pageY };
                }),
                scan(
                    (_, { pageX, pageY }) => {
                      return {
                        differenceX: Math.round(startEvent.touches[0].pageX - pageX),
                        differenceY: Math.round(startEvent.touches[0].pageY - pageY),
                      };
                    },
                    { differenceX: 0, differenceY: 0 },
                ),
                filter(({ differenceX, differenceY }) => {
                  return Math.abs(differenceX) >= toleranceXY && Math.abs(differenceY) < toleranceXY;
                }),
            ),
        ),
    );

    this.swiperSub = swipe$.pipe(filter(() => !this.isPhotosLoaded)).subscribe(({ differenceX }) => {
      if (differenceX > 0) {
        this.nextPhoto();
      } else {
        this.prevPhoto();
      }
    });

    this.isSwiperObservableInit = true;
  }

  private renderSwiperBullets(): void {
    const bulletsRoot = this.swiperBullets?.nativeElement;
    if (!bulletsRoot) {
      return;
    }

    const total = this.tour.photosCount;

    for (let i = 1; i <= total; i++) {
      const span: HTMLSpanElement = this.render.createElement('span');
      span.classList.add('bullet');
      if (i === 1) {
        span.classList.add('bullet--active');
        span.classList.add('bullet--sm');
      }
      if (i > 1 && i < 5) {
        span.classList.add('bullet--sm');
      }
      if (i >= 5) {
        span.classList.add('bullet--xs');
      }

      span.dataset.index = i.toString();

      this.render.appendChild(bulletsRoot, span);
    }
  }

  private initSwiper(): void {
    if (!this.swiperGallery) {
      return;
    }

    this.swiperInstance?.destroy(true, true);
    this.swiperInstance = new Swiper(this.swiperGallery.nativeElement, {
      ...this.swipperConfig,
      initialSlide: this.shouldOpenFromSecondSlide ? 1 : 0,
      on: {
        init: () => this.swiperInit(),
        slideNextTransitionStart: () => this.slideNextTransitionStart(),
        slidePrevTransitionStart: () => this.slidePrevTransitionStart(),
      },
    });
  }

  private nextPhotoBulletAnimation(): void {
    const bulletsRoot = this.swiperBullets?.nativeElement;
    if (!bulletsRoot) {
      return;
    }

    const total = this.tour.photosCount;
    const active = bulletsRoot.querySelector<HTMLElement>('.bullet--active');
    if (!active) {
      return;
    }
    active.classList.remove('bullet--active');
    active.classList.add('bullet--sm');

    const index = this.currentPhotoIndex === 1 ? this.currentPhotoIndex + 1 : this.currentPhotoIndex;
    const nextBullet = bulletsRoot.querySelector<HTMLElement>('[data-index="' + index + '"]');
    if (!nextBullet) {
      return;
    }
    nextBullet.classList.add('bullet--active');
    nextBullet.classList.add('bullet--sm');
    nextBullet.classList.remove('bullet--xs');

    const bullets = bulletsRoot.querySelectorAll<HTMLElement>('.bullet');

    if (index - 2 >= 1 && total - index > 1) {
      const bullet = bulletsRoot.querySelector<HTMLElement>('[data-index="' + (index - 2) + '"]');
      bullet?.classList.add('bullet--xs');
    }

    if (index > 3 && index < total) {
      const smBullet = bulletsRoot.querySelector<HTMLElement>('[data-index="' + (index + 1) + '"]');
      smBullet?.classList.add('bullet--sm');
      smBullet?.classList.remove('bullet--xs');

      if (index + 1 < total) {
        bullets.forEach((bullet) => {
          let left = 11;
          if (bullet.style.left.length > 0) {
            const str = bullet.style.left.substr(1, bullet.style.left.length - 3);
            left = parseInt(str === '' ? '0' : str, 10) + 11;
          }

          this.render.setStyle(bullet, 'left', '-' + left + 'px');
        });
      }
    }
  }

  private prevPhotoBulletAnimation(): void {
    const bulletsRoot = this.swiperBullets?.nativeElement;
    if (!bulletsRoot) {
      return;
    }

    const total = this.tour.photosCount;
    const active = bulletsRoot.querySelector<HTMLElement>('.bullet--active');
    if (!active) {
      return;
    }
    active.classList.remove('bullet--active');
    active.classList.add('bullet--sm');

    const index = this.currentPhotoIndex;
    const prevBullet = bulletsRoot.querySelector<HTMLElement>('[data-index="' + index + '"]');
    if (!prevBullet) {
      return;
    }
    prevBullet.classList.add('bullet--active');
    prevBullet.classList.add('bullet--sm');
    prevBullet.classList.remove('bullet--xs');

    const bullets = bulletsRoot.querySelectorAll<HTMLElement>('.bullet');

    if (index - 2 >= 1 && total - index > 1) {
      const bullet = bulletsRoot.querySelector<HTMLElement>('[data-index="' + (index + 2) + '"]');
      bullet?.classList.add('bullet--xs');
    }

    if (index >= 2 && index < total) {
      const smBullet = bulletsRoot.querySelector<HTMLElement>('[data-index="' + (index - 1) + '"]');
      smBullet?.classList.add('bullet--sm');
      smBullet?.classList.remove('bullet--xs');

      if (index + 3 < total && index >= 3) {
        bullets.forEach((bullet) => {
          let left = 11;

          if (bullet.style.left.length > 0) {
            const str = bullet.style.left.substr(1, bullet.style.left.length - 3);
            left = parseInt(str === '' ? '0' : str, 10) - 11;
          }

          this.render.setStyle(bullet, 'left', '-' + left + 'px');
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.swiperSub?.unsubscribe();
    this.swiperInstance?.destroy(true, true);
    this.swiperInstance = null;
  }

  private setActiveBullet(index: number): void {
    const bulletsRoot = this.swiperBullets?.nativeElement;
    if (!bulletsRoot) {
      return;
    }

    const active = bulletsRoot.querySelector<HTMLElement>('.bullet--active');
    if (active) {
      active.classList.remove('bullet--active');
      active.classList.add('bullet--sm');
    }

    const nextBullet = bulletsRoot.querySelector<HTMLElement>('[data-index="' + index + '"]');
    if (!nextBullet) {
      return;
    }

    nextBullet.classList.add('bullet--active');
    nextBullet.classList.add('bullet--sm');
    nextBullet.classList.remove('bullet--xs');
  }

  private isSamePhoto(left: string | null | undefined, right: string | null | undefined): boolean {
    if (!left || !right) {
      return false;
    }

    return this.normalizePhotoSrc(left) === this.normalizePhotoSrc(right);
  }

  private normalizePhotoSrc(value: string): string {
    return value.trim().split('#')[0].split('?')[0];
  }
}

export { TourImageMobileComponent as TourImageComponent };
